// Component Registry - Centralized definition of all components and their permission requirements
// This file defines the default visibility rules for all components in the application

export const COMPONENT_REGISTRY = {
  'admin.help-manager': {
    section: 'admin',
    description: 'Help content and visibility manager',
    viewRoles: ['Admin'],
    editRoles: ['Admin'],
    environments: ['all']
  },
  // Navigation Components
  'home.forms': {
    section: 'navigation',
    description: 'Forms Home button and workspace. View allows read-only access; Edit allows purchase request submission and attachment uploads. Hidden by default for all roles.',
    viewRoles: [],
    editRoles: [],
    environments: ['all']
  },
  'home.apps-portal': {
    section: 'navigation',
    description: 'Apps Portal navigation button',
    defaultRoles: ['Admin', 'AppsManager'],
    environments: ['all']
  },
  'home.production-portal': {
    section: 'navigation', 
    description: 'Production Portal navigation button',
    defaultRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    environments: ['all']
  },
  'home.panel-shop': {
    section: 'navigation',
    description: 'Panel Shop navigation button',
    defaultRoles: ['Admin'],
    environments: ['all']
  },
  'home.sales-portal': {
    section: 'navigation',
    description: 'Sales Portal navigation button',
    viewRoles: ['Admin', 'Sales', 'Apps'],
    editRoles: ['Admin', 'Sales'],
    environments: ['all']
  },
  'home.global-search': {
    section: 'navigation',
    description: 'Global Search navigation button', 
    defaultRoles: ['Admin'],
    environments: ['all']
  },
  'home.user-home': {
    section: 'navigation',
    description: 'User Home navigation button',
    defaultRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling'],
    environments: ['all']
  },
  'home.north-prairie': {
    section: 'navigation',
    description: 'North Prairie navigation button',
    defaultRoles: ['Admin', 'NorthPrairie'],
    environments: ['all']
  },
  'home.engineering-tools': {
    section: 'navigation',
    description: 'Engineering Tools navigation button',
    defaultRoles: ['Admin', 'Engineer', 'EngineerManager'],
    environments: ['all']
  },
  'home.production-schedule': {
    section: 'navigation',
    description: 'Production Schedule navigation button',
    defaultRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    environments: ['all']
  },
  'home.controls': {
    section: 'navigation',
    description: 'Controls workspace navigation button',
    defaultRoles: ['Admin'],
    environments: ['all']
  },
  'home.assembly': {
    section: 'navigation',
    description: 'Assembly schedule navigation button',
    viewRoles: ['Admin', 'Scheduling', 'AssemblySchedule'],
    environments: ['all']
  },
  'home.shipping': {
    section: 'navigation', description: 'Shipping link on the Home screen under Production. Workspace and tab access are managed separately with shipping.* policies.',
    viewRoles: ['Admin', 'Scheduling'], environments: ['all']
  },
  'shipping.container': {
    section: 'shipping', description: 'Access to the Shipping workspace and its routes. Users also need view access to at least one Shipping tab.',
    viewRoles: ['Admin', 'Scheduling'], environments: ['all']
  },
  'shipping.display': {
    section: 'shipping', description: 'Shipping Display tab. Edit enables Transport Mode and shipping workflow cell edits outside Full Screen. Full Screen remains read-only with five-minute automatic refresh.',
    viewRoles: ['Admin', 'Scheduling'], editRoles: ['Admin', 'Scheduling'], environments: ['all']
  },
  'shipping.end-of-shift': {
    section: 'shipping', description: 'End of Shift Log tab with shipment, delivery, transporter, and freight details. Includes an End of Shift Summary button to copy the formatted table. Edit enables Delivery Date cell edits; quote selection is managed in Data Entry.',
    viewRoles: ['Admin', 'Scheduling'], editRoles: ['Admin', 'Scheduling'], environments: ['all']
  },
  'shipping.shipping-details': {
    section: 'shipping', description: 'Shipping Details tab with job, Ship To, freight details, and quote email drafts.',
    viewRoles: ['Admin', 'Scheduling'], environments: ['all']
  },
  'shipping.data-entry': {
    section: 'shipping', description: 'Data Entry tab. View grants read-only access; Edit enables shipping field cell/modal edits and Transport Mode cell edits to project TransportType.',
    viewRoles: ['Admin', 'Scheduling'], editRoles: ['Admin', 'Scheduling'], environments: ['all']
  },
  'shipping.change-log': {
    section: 'shipping', description: 'Read-only Shipping Change Log tab with field history, user, role, job, date, and search filters.',
    viewRoles: ['Admin'], environments: ['all']
  },
  'shipping.packing-list-status': {
    section: 'shipping', description: 'Read-only Packing List Status tab showing assembly and packing list statuses alongside project details for jobs with PackDue.',
    viewRoles: ['Admin', 'Scheduling'], environments: ['all']
  },
  'shipping.on-hold-ready-to-ship': {
    section: 'shipping',
    description: 'Read-only On Hold Ready to Ship tab. Uses the On Hold Jobs layout and shows only jobs with JobStatus Hold/ Ready to Ship.',
    viewRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'admin.container': {
    section: 'admin',
    description: 'Admin page route and navigation entry',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  'admin.signalr-test': {
    section: 'admin',
    description: 'SignalR diagnostics. View allows connection tests; Edit allows sending diagnostic broadcasts.',
    viewRoles: ['Admin'],
    editRoles: ['Admin'],
    environments: ['all']
  },
  'admin.component-policies': {
    section: 'admin',
    description: 'Component Policies admin tab',
    viewRoles: ['Admin'],
    editRoles: ['Admin'],
    environments: ['all']
  },
  'admin.global-variables': {
    section: 'admin',
    description: 'Global Variables admin tool',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'admin.dynamic-table-editor': {
    section: 'admin',
    description: 'Dynamic Table Editor admin tool',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'admin.user-preferences': {
    section: 'admin',
    description: 'User Preferences admin tool',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  // User Page Components
  'user.assigned-jobs': {
    section: 'user',
    description: 'Assigned Jobs with TBD section',
    viewRoles: ['Admin', 'Apps', 'AppsManager'],
    environments: ['all']
  },
  'user.assigned-jobs.project-manager-column': {
    section: 'user',
    description: 'Project Manager column in Assigned Jobs',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  'user.change-order-tasks': {
    section: 'user', 
    description: 'Open Change Order Tasks section',
    viewRoles: ['Admin', 'Engineer', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlEngineer'],
    environments: ['all']
  },
  'user.assigned-engineering-jobs': {
    section: 'user',
    description: 'Assigned Engineering Jobs section',
    viewRoles: ['Admin', 'Engineer', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlEngineer'],
    environments: ['all']
  },
  'user.mechanical-review': {
    section: 'user',
    description: 'Mechanical Review section',
    viewRoles: ['Admin', 'Engineer', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlEngineer'],
    environments: ['all']
  },
  'user.pto-management': {
    section: 'user',
    description: 'PTO Management section',
    viewRoles: ['Admin', 'Engineer', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlEngineer'],
    environments: ['all']
  },
  'user.on-time-delivery': {
    section: 'reports',
    description: 'Production On Time Delivery report',
    viewRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'user.csm-enclosure-table': {
    section: 'user',
    description: 'CSM Enclosure Table section',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  'user.np-jobs': {
    section: 'user',
    description: 'NP Jobs section for production items categorization',
    viewRoles: ['Admin', 'NorthPrairie'],
    environments: ['all']
  },
  'npjobs.collector': {
    section: 'npjobs',
    description: 'NP Jobs Collector for categorizing manufactured items',
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'np.container': {
    section: 'npjobs',
    description: 'North Prairie container route',
    viewRoles: ['Admin', 'NorthPrairie'],
    environments: ['all']
  },
  'sales.container': {
    section: 'sales',
    description: 'Sales Portal route container',
    viewRoles: ['Admin', 'Sales', 'Apps'],
    editRoles: ['Admin', 'Sales'],
    environments: ['all']
  },

  // Job Schedule Components
  'schedule.job-schedule': {
    section: 'schedule',
    description: 'Enhanced Job Schedule Table tab',
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.engineering-schedule': {
    section: 'schedule',
    description: 'Engineering Schedule tab',
    viewRoles: ['Admin', 'Scheduling', 'Engineer', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlEngineer'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.engineering-optimizer': {
    section: 'schedule',
    description: 'Engineering Schedule Optimizer tab',
    viewRoles: ['Admin', 'Scheduling'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.ready-to-schedule': {
    section: 'schedule',
    description: 'Ready to Schedule tab',
    viewRoles: ['Admin', 'Scheduling'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.move-requests': {
    section: 'schedule',
    description: 'Move Requests tab',
    viewRoles: ['Admin', 'Scheduling'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.release-pending-cso': {
    section: 'schedule',
    description: 'Release Pending CSO tab',
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.on-hold-jobs': {
    section: 'schedule',
    description: 'On Hold Jobs tab',
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.pending-in-portal': {
    section: 'schedule',
    description: 'CSO (Pending in Portal) tab',
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.canceled-jobs': {
    section: 'schedule',
    description: 'Canceled Jobs tab',
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.shipped-jobs': {
    section: 'schedule',
    description: 'Shipped Jobs tab',
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly', 'AppsManager'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.search': {
    section: 'schedule',
    description: 'Job Schedule Search tab',
    viewRoles: ['Admin', 'Scheduling'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.full-release-mismatch': {
    section: 'schedule',
    description: 'Full Release Status Mismatch tab',
    viewRoles: ['Admin', 'Scheduling'],
    editRoles: ['Admin', 'Scheduling'],
    environments: ['all']
  },
  'schedule.change-log': {
    section: 'schedule',
    description: 'Schedule Change Log tab',
    viewRoles: ['Admin'],
    editRoles: ['Admin'],
    environments: ['all']
  },
  'controls.container': {
    section: 'controls',
    description: 'Controls workspace route container',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  'controls.schedule': {
    section: 'controls',
    description: 'Controls schedule tab',
    viewRoles: ['Admin'],
    editRoles: ['Admin'],
    environments: ['all']
  },
  'controls.calendar': {
    section: 'controls',
    description: 'Controls calendar tab',
    viewRoles: ['Admin'],
    editRoles: ['Admin'],
    environments: ['all']
  },
  'assembly.container': {
    section: 'assembly',
    description: 'Assembly workspace route container',
    viewRoles: ['Admin', 'Scheduling', 'AssemblySchedule'],
    environments: ['all']
  },
  'assembly.schedule': {
    section: 'assembly',
    description: 'Assembly schedule table',
    viewRoles: ['Admin', 'Scheduling', 'AssemblySchedule'],
    editRoles: ['Admin', 'Scheduling', 'AssemblySchedule'],
    environments: ['all']
  },
  'assembly.assemblers': {
    section: 'assembly',
    description: 'Assembly assembler dropdown manager',
    viewRoles: ['Admin', 'Scheduling', 'AssemblySchedule'],
    editRoles: ['Admin', 'Scheduling', 'AssemblySchedule'],
    environments: ['all']
  },
  'assembly.change-log': {
    section: 'assembly',
    description: 'Assembly schedule change log',
    viewRoles: ['Admin'],
    environments: ['all']
  },

  // Portal Components (examples for future use)
  'portal.project-info': {
    section: 'portal',
    description: 'Project Information form',
    editRoles: ['Admin', 'Apps'],
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly'],
    environments: ['all']
  },
  'portal.project-info.project-status': {
    section: 'portal',
    description: 'Project Status field in Project Info',
    editRoles: ['Admin', 'Apps'],
    viewRoles: ['Admin', 'Apps', 'Engineer', 'Scheduling', 'ReadOnly'],
    environments: ['all']
  },
  'portal.job-list.create-job': {
    section: 'portal',
    description: 'Create New Job action on the job list',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'portal.zone-buttons.create-zone2': {
    section: 'portal',
    description: 'Create Zone 2 action from zone buttons',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'portal.additional-items.release-form': {
    section: 'portal',
    description: 'Release for Scheduling form section in Additional Items and Forms',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'portal.forms.cso-feedback': {
    section: 'portal',
    description: 'CSO Feedback in job Forms. View allows reading submissions; Edit allows new submissions and submitter-owned content/attachments. Status changes also require the Apps role.',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'portal.forms.ul': {
    section: 'portal',
    description: 'Job UL forms. View allows reading checklists; Edit allows adding, autosaving, and submitting unlocked forms.',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'portal.forms.panel-shop': {
    section: 'portal',
    description: 'Job Panel Shop forms. View allows reading checklists; Edit allows adding, autosaving, and submitting unlocked forms.',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'portal.additional-items.vt-calculator': {
    section: 'portal',
    description: 'VT Loss Calculator section in Additional Items',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  'portal.change-log.add-to-change-order': {
    section: 'portal',
    description: 'Add Change Log entry to Change Orders action',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },
  'portal.ship-early.manage': {
    section: 'portal',
    description: 'Create and manage Ship Early forms',
    viewRoles: ['Admin', 'Apps'],
    editRoles: ['Admin', 'Apps'],
    environments: ['all']
  },

  // Apps Page Components
  'apps.bookings-log': {
    section: 'apps',
    description: 'Bookings Log table and management',
    editRoles: ['Admin', 'Apps'],
    viewRoles: ['Admin', 'Apps', 'Sales', 'ReadOnly', 'AppsManager'],
    environments: ['all']
  },
  'apps.sales-targets': {
    section: 'apps',
    description: 'Sales Targets management and tracking',
    editRoles: ['Admin', 'Apps'],
    viewRoles: ['Admin', 'Apps', 'Sales', 'ReadOnly', 'AppsManager'],
    environments: ['all']
  },
  'apps.activity-log-change-log': {
    section: 'apps',
    description: 'Activity Log change log tab',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  
  // Engineering Task Components
  'engineering.task-management': {
    section: 'engineering',
    description: 'Engineering Task Management - Full CRUD operations with filters',
    editRoles: ['Admin', 'EngineerManager'],
    viewRoles: ['Admin', 'EngineerManager'],
    environments: ['all']
  },
  'engineering.user-tasks': {
    section: 'engineering',
    description: 'User-specific engineering tasks view',
    editRoles: ['Admin', 'EngineerManager', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlEngineer'],
    viewRoles: ['Admin', 'EngineerManager', 'MechanicalEngineer', 'ElectricalEngineer', 'ControlEngineer'],
    environments: ['all']
  },
  'engineering.protected-time': {
    section: 'engineering',
    description: 'Engineering Protected Time Management - Manage protected time blocks for engineers',
    editRoles: ['Admin', 'EngineerManager'],
    viewRoles: ['Admin', 'EngineerManager'],
    environments: ['all']
  },
  'engineering.mechanical-review': {
    section: 'engineering',
    description: 'Mechanical Review - Review and approve mechanical engineering work',
    editRoles: ['Admin', 'EngineerManager'],
    viewRoles: ['Admin', 'EngineerManager', 'MechanicalEngineer'],
    environments: ['all']
  },
  'engineering.calculators': {
    section: 'engineering',
    description: 'Engineering calculators tab',
    viewRoles: ['Admin', 'Engineer', 'EngineerManager'],
    environments: ['all']
  },
  'engineering.calendar': {
    section: 'engineering',
    description: 'Engineering calendar tab',
    viewRoles: ['Admin', 'Engineer', 'EngineerManager'],
    environments: ['all']
  },
  'engineering.assigned-jobs': {
    section: 'engineering',
    description: 'Assigned engineering jobs tab',
    viewRoles: ['Admin', 'Engineer', 'EngineerManager'],
    environments: ['all']
  },
  'engineering.pto-management': {
    section: 'engineering',
    description: 'Engineering PTO management tab',
    viewRoles: ['Admin', 'Engineer', 'EngineerManager'],
    environments: ['all']
  },
  'engineering.mechanical-reference': {
    section: 'engineering',
    description: 'Mechanical reference tab',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  'engineering.panel-sizing': {
    section: 'engineering',
    description: 'Panel sizing tab',
    viewRoles: ['Admin'],
    environments: ['all']
  },
  'engineering.change-order-tasks': {
    section: 'engineering',
    description: 'Engineering change order tasks tab',
    viewRoles: ['Admin', 'Engineer', 'EngineerManager'],
    environments: ['all']
  },
  'home.engineering-design-documents': {
    section: 'navigation',
    description: 'Engineering Design Documents navigation button',
    requiresAuth: true,
    environments: ['all']
  }
};

// Auto-derived constant map — use for IDE autocomplete and typo prevention
// e.g. canView(COMPONENT_KEYS.ENGINEERING_CALCULATORS)
export const COMPONENT_KEYS = Object.freeze(
  Object.fromEntries(
    Object.keys(COMPONENT_REGISTRY).map((k) => [
      k.replace(/[.-]/g, '_').toUpperCase(),
      k,
    ])
  )
);

// Helper function to get all registered components
export const getAllComponents = () => {
  return Object.keys(COMPONENT_REGISTRY);
};

// Helper function to get components by section
export const getComponentsBySection = (section) => {
  return Object.entries(COMPONENT_REGISTRY)
    .filter(([_, config]) => config.section === section)
    .reduce((acc, [key, config]) => {
      acc[key] = config;
      return acc;
    }, {});
};

// Helper function to get all sections
export const getAllSections = () => {
  const sections = new Set();
  Object.values(COMPONENT_REGISTRY).forEach(config => {
    sections.add(config.section);
  });
  return Array.from(sections);
};

// Permission levels enum
export const PERMISSION_LEVELS = {
  HIDDEN: 'hidden',
  VIEW: 'view', 
  EDIT: 'edit'
};

// User-facing labels for roles whose internal identifiers omit spaces.
export const ROLE_LABELS = {
  AssemblySchedule: 'Assembly Schedule',
};

// Role hierarchy for fallback logic
export const ROLE_HIERARCHY = {
  'Admin': 100,
  'Apps': 80,
  'EngineerManager': 75,
  'Engineer': 70,
  'ControlEngineer': 65,
  'ElectricalEngineer': 65,
  'MechanicalEngineer': 65,
  'Scheduling': 60,
  'AssemblySchedule': 60,
  'NorthPrairie': 55,
  'TestPit': 50,
  'PSN': 50,
  'Sales': 50,
  'Accounting': 45,
  'ReadOnly': 10,
  'AppsManager': 11
};
